import { checkConsumerDobOver21 } from '@imprint-payments/imprint-lib';
import {
  colors,
  ImpButton,
  ImpContainer,
  ImpFlex,
  ImpIcon,
  ImpInput,
  ImpSelect,
  ImpSpacer,
  ImpText,
  spacing,
} from '@imprint-payments/imprint-ui';
import { useEffect, useRef, useState } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { useLogEventOnce } from '../../analytics/analyticsUtil';
import { submitApplication, submitAppWithSSN9 } from '../../api/api';
import {
  setLoading,
  setShowLinkHHScreen,
} from '../../app/applicationScreenSlice';
import { setConsumerFactor } from '../../app/consumerInfoSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import { setFullSsnError } from '../../app/validatorSlice';
import lock from '../../assets/images/lock.png';
import { ANALYTICS } from '../../firebase/firebase';
import {
  trackPostieEvent,
  usePostieTrackingInit,
} from '../../hooks/usePostieTrackingPixel';
import { ConsumerFactors } from '../../types';
import { formatFullSSN, formatSSN } from '../../utils/formatUtils';
import {
  clearHomePageState,
  getOfferUUIDs,
  handleFormSubmit,
} from '../../utils/functionUtils';
import { PRODUCT_UUID } from '../../utils/productUUIDConstants';
import { useSDKContext } from '../../utils/sdk';
import { SESSION_STORAGE } from '../../utils/sessionStorageConstants';
import {
  getValidationString,
  hideWhileTyping,
  noError,
  validateAnnualIncome,
  validateFullSSN,
  validateHousing,
  ValidationResponse,
} from '../../utils/validateUtils';
import { HousingDisclosure, IncomeDisclosure } from './Disclosures';
import { formatUSD, toUSDKeyDown, useFormInput } from './useFormInput';

enum IncomeType {
  Employment = 'Employment',
  SelfEmployment = 'SelfEmployment',
  Retirement = 'Retirement',
  Unemployment = 'Unemployment',
  GovtAssistance = 'GovtAssistance',
  OtherIncome = 'OtherIncome',
}

const incomeTypes = [
  IncomeType.Employment,
  IncomeType.SelfEmployment,
  IncomeType.Retirement,
  IncomeType.Unemployment,
  IncomeType.GovtAssistance,
  IncomeType.OtherIncome,
];

enum HousingType {
  Rent = 'Rent',
  Own = 'Own',
  Other = 'Other',
}

const housingTypes = [HousingType.Rent, HousingType.Own, HousingType.Other];

const getHousingTypeLabel = (val: string, language: any) => {
  switch (val) {
    case HousingType.Rent:
      return language.housingTypeOption[0];
    case HousingType.Own:
      return language.housingTypeOption[1];
    case HousingType.Other:
      return language.housingTypeOption[2];
    default:
      return '';
  }
};

const getIncomeTypeLabel = (val: string, language: any) => {
  switch (val) {
    case IncomeType.Employment:
      return language.incomeSourceOptionV2[0];
    case IncomeType.SelfEmployment:
      return language.incomeSourceOptionV2[1];
    case IncomeType.Retirement:
      return language.incomeSourceOptionV2[2];
    case IncomeType.Unemployment:
      return language.incomeSourceOptionV2[3];
    case IncomeType.GovtAssistance:
      return language.incomeSourceOptionV2[4];
    case IncomeType.OtherIncome:
      return language.incomeSourceOptionV2[5];
    default:
      return '';
  }
};

interface IncomeHousingV2Props {
  submitButtonState: 'enabled' | 'disabled' | 'submitting';
  setSubmitButtonState: (
    state: IncomeHousingV2Props['submitButtonState']
  ) => void;
}

export default function IncomeHousingV2({
  submitButtonState,
  setSubmitButtonState,
}: IncomeHousingV2Props) {
  const {
    language,
    merchantData,
    consumerInfo,
    dispatch,
    loginState,
    appStatus,
    appScreenState,
  } = useImpState();
  const logEventOnce = useLogEventOnce();
  useEffect(() => {
    logEventOnce(EVENT.INCOME_HOUSING_VIEWED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validator: any = useAppSelector((state) => state.validatorSelector);

  const storedSsn4 = useAppSelector(
    (state) => state.consumerInfoSelector.consumerProfile.ssn4
  );
  const [displayFullSsn, setDisplayFullSsn] = useState('');
  const [fullSsn, setFullSsn] = useState('');
  const [disabledInputField, setDisabledInputField] = useState(
    appScreenState.requireFullSSN || !loginState.loginFlow.loginError
  );
  const [isMultiCardApplicationFlow, setIsMultiCardApplicationFlow] =
    useState(false);

  const consumerFactors = consumerInfo.consumerFactor;

  const [canContinue, setCanContinue] = useState(false);

  const [incomeSource, setIncomeSource] = useState(consumerFactors.incomeType);
  const [housingType, setHousingType] = useState(consumerFactors.housingType);

  const [ssaChecked, setSsaChecked] = useState(true);

  const [housingCost, isHousingCostValid, housingCostInputProps] = useFormInput(
    {
      validator: validateHousing,
      formatter: formatUSD,
      cleanFormattedValue: (val: string) => val.replace(/[^0-9]/g, ''),
      onKeyDown: toUSDKeyDown,
      defaultValue: consumerFactors.housingCost,
      maxLength: 7,
      // accounts for formatting
      maxLengthBuffer: 3,
    }
  );
  const [income, isIncomeValid, incomeInputProps] = useFormInput({
    validator: validateAnnualIncome,
    formatter: formatUSD,
    cleanFormattedValue: (val: string) => val.replace(/[^0-9]/g, ''),
    onKeyDown: toUSDKeyDown,
    defaultValue: consumerFactors.income,
    maxLength: 7,
    maxLengthBuffer: 3,
  });

  const { setResult } = useSDKContext();

  useEffect(() => {
    // everything else is irrelevant in the full ssn4 case
    if (appScreenState.requireFullSSN) {
      setCanContinue(validator.fullSSNError === ValidationResponse.valid);
      return;
    }

    setCanContinue(
      (isMultiCardApplicationFlow ||
        !appScreenState.requireFullSSN ||
        validator.fullSSNError === ValidationResponse.valid) &&
        incomeSource !== '' &&
        isIncomeValid &&
        housingType !== '' &&
        isHousingCostValid
    );
  }, [
    validator,
    appScreenState,
    isMultiCardApplicationFlow,
    incomeSource,
    income,
    isIncomeValid,
    housingType,
    housingCost,
    isHousingCostValid,
    appScreenState.requireFullSSN,
  ]);

  function backClick() {
    clearHomePageState(dispatch);
  }

  useEffect(() => {
    if (appScreenState.showWelcomeBackCard) {
      ANALYTICS.logEvent(EVENT.RETURNING_USER_INCOME_HOUSING_ID_VIEWED, {
        product_uuid: merchantData.productUUID,
      });
      setIsMultiCardApplicationFlow(loginState.multiCardUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appScreenState.showWelcomeBackCard, loginState.multiCardUser]);

  useEffect(() => {
    const validationResponse = validateFullSSN(fullSsn, storedSsn4);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setFullSsnError(validationResponse));
    } else {
      if (fullSsn !== '') {
        dispatch(setFullSsnError(ValidationResponse.empty));
      } else {
        dispatch(setFullSsnError(ValidationResponse.invalidSSN));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullSsn]);

  useEffect(() => {
    window.history.pushState(
      'fake-route',
      document.title,
      window.location.href
    );
    // eslint-disable-next-line no-restricted-globals
    addEventListener('popstate', backClick);
    return () => {
      // eslint-disable-next-line no-restricted-globals
      removeEventListener('popstate', backClick);
      if (window.history.state === 'fake-route') {
        window.history.back();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (appScreenState.requireFullSSN) {
      ANALYTICS.logEvent(EVENT.INCOME_HOUSING_FULL_SSN_VIEWED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDisabledInputField(
      appScreenState.requireFullSSN || !loginState.loginFlow.loginError
    );
    if (!appScreenState.requireFullSSN) {
      setDisabledInputField(false);
    }
  }, [loginState.loginFlow.loginError, appScreenState.requireFullSSN]);

  useEffect(() => {
    const consumerFactor = consumerInfo.consumerFactor;
    if (
      merchantData.productUUID === PRODUCT_UUID.HH &&
      appScreenState.eligibilityVerified &&
      consumerFactor.income !== '' &&
      consumerFactor.incomeType !== '' &&
      consumerFactor.housingType !== '' &&
      consumerFactor.housingCost !== ''
    ) {
      submit(consumerFactor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerInfo.consumerFactor]);

  useEffect(() => {
    if (appScreenState.loading || appScreenState.isSubmittingApp) {
      setSubmitButtonState('submitting');
    } else if (!canContinue) {
      setSubmitButtonState('disabled');
    } else {
      setSubmitButtonState('enabled');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canContinue, appScreenState.loading, appScreenState.isSubmittingApp]);
  useSaveOnUnmount({
    income,
    incomeType: incomeSource,
    housingType,
    housingCost,
  });

  usePostieTrackingInit();

  const handleSubmit = async () => {
    ANALYTICS.logEvent(EVENT.INCOME_HOUSING_SUBMIT_CLICKED);
    trackPostieEvent('trackStructEvent', 'application', 'completed');

    if (appScreenState.showWelcomeBackCard) {
      ANALYTICS.logEvent(EVENT.RETURNING_USER_INCOME_HOUSING_SUBMIT_CLICKED, {
        product_uuid: merchantData.productUUID,
      });
    }
    let consumerFactorData = {
      income: '',
      incomeType: '',
      housingType: '',
      housingCost: '',
    };

    if (!appScreenState.requireFullSSN) {
      consumerFactorData = {
        income,
        incomeType: incomeSource,
        housingType,
        housingCost,
      };

      dispatch(setConsumerFactor(consumerFactorData));
    }

    submit(consumerFactorData);
  };

  const submit = (consumerFactor: ConsumerFactors) => {
    // Submit with SSN9
    if (appScreenState.requireFullSSN) {
      dispatch(setLoading(true));
      dispatch(
        submitAppWithSSN9(
          {
            token: consumerInfo.auth.token,
            applicationUUID: consumerInfo.auth.applicationUUID,
            ssn9: formatSSN(fullSsn),
          },
          setResult
        )
      );
      return;
    }

    // Verify RC club email for HH
    if (
      merchantData.productUUID === PRODUCT_UUID.HH &&
      !appScreenState.eligibilityVerified
    ) {
      dispatch(setLoading(false));
      dispatch(setShowLinkHHScreen(true));
      return;
    }

    // Normal submit
    dispatch(setLoading(true));
    dispatch(
      submitApplication({
        rewardsInfo: consumerInfo.rewardsInfo,
        consumer: loginState.multiCardUser
          ? consumerInfo.consumerProfile
          : {
              ...consumerInfo.consumerProfile,
              ssn4: storedSsn4,
            },
        consumerFactor,
        productUUID: merchantData.productUUID,
        isMultiCard: loginState.multiCardUser,
        offerUUIDs: getOfferUUIDs(),
        setResult,
        SSADisclosureAccepted: ssaChecked,
      })
    );
  };

  const formatMaskedSsn = (ssn: string): string => {
    const filtered = ssn.replace(/[^\d]/g, '');

    if (filtered.length <= 3) {
      return 'X'.repeat(ssn.length);
    }

    if (filtered.length === 4) {
      return 'XXX-X';
    }

    if (filtered.length === 5) {
      return 'XXX-XX';
    }

    return 'XXX-XX-' + filtered.substring(5);
  };

  const handleFullSsnChange = (e: any) => {
    if (e.nativeEvent.inputType === 'deleteContentBackward') {
      const filteredFullSsn = fullSsn.replace(/[^\d]/g, '');
      const filteredVal = e.target.value.replace(/[^X\d]/g, '');

      const diff = filteredFullSsn.length - filteredVal.length;

      const newSsn = filteredFullSsn.substring(
        0,
        filteredFullSsn.length - diff
      );

      setFullSsn(formatFullSSN(newSsn));
      setDisplayFullSsn(formatMaskedSsn(newSsn));
      return;
    }

    const inputValue = e.target.value;
    const lastChar = inputValue[inputValue.length - 1];

    const newSsn =
      lastChar && lastChar.match(/[0-9]/) ? fullSsn + lastChar : fullSsn;

    setFullSsn(formatFullSSN(newSsn));
    setDisplayFullSsn(formatMaskedSsn(newSsn));
  };

  const handleSsn9KeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Backspace',
      'Delete',
      'Enter',
    ];
    const isSelect = (e.ctrlKey || e.metaKey) && 'a' === e.key.toLowerCase();

    if (!allowedKeys.includes(e.key) && isNaN(parseInt(e.key)) && !isSelect) {
      e.preventDefault();
    }
  };

  const ssnView = () => {
    if (appScreenState.requireFullSSN) {
      return (
        <ImpContainer>
          <ImpInput
            label={language.fullSSN}
            value={displayFullSsn}
            maxLength={11}
            id="fullSSN"
            onChange={handleFullSsnChange}
            showError={!noError.includes(validator.fullSSNError)}
            errorMessage={
              fullSsn.length < 9
                ? language.fullSSNErrmsg
                : getValidationString(validator.fullSSNError, language)
            }
            trailingIcon={<ImpIcon iconSrc={lock} height="24px" width="24px" />}
            onBlur={() => {
              dispatch(setFullSsnError(validateFullSSN(fullSsn, storedSsn4)));
            }}
            onKeyDown={(e) => handleSsn9KeyDown(e)}
            type="tel"
            maxWidth="100%"
            caption={language.ssnDisclosure}
          />
        </ImpContainer>
      );
    }
  };

  const getDefaultVal = (
    val: string,
    matcher: (val: string, language: any) => '' | HousingType | IncomeType
  ) => {
    if (val === '') return undefined;

    return {
      value: val,
      label: matcher(val, language),
    };
  };

  const disabledInput =
    disabledInputField ||
    (appStatus.reApplicationInApp && appScreenState.requireFullSSN) ||
    submitButtonState === 'submitting';

  return (
    <ImpContainer maxWidth="368px">
      <form onSubmit={handleFormSubmit}>
        <ImpSpacer height="xl" />
        <ImpContainer textAlign={'center'}>
          <ImpFlex
            fluid
            direction={'column'}
            align={'center'}
            justify={'center'}
          >
            <ImpText typography={'headline3'}>
              {language.financialInfoTitle}
            </ImpText>
            <ImpSpacer height={'xs'} />
            <ImpText
              typography={'body2'}
              color={colors.content.onSurfaceVariant}
            >
              {language.financialInfoSubtitle}
            </ImpText>
          </ImpFlex>
        </ImpContainer>
        <ImpSpacer height={'xl'} />
        <ImpFlex direction="column" align="normal" gap="m">
          <ImpSelect
            label={language.incomeSource}
            id="incomeSource"
            options={language.incomeSourceOptionV2.map(
              (val: string, index: number) => {
                return { value: incomeTypes[index], label: val };
              }
            )}
            onOptionChange={(val) => {
              ANALYTICS.logEvent(EVENT.INCOME_HOUSING_FORM_FILLED_OUT, {
                field: 'primary_income_source',
              });
              setIncomeSource(val);
            }}
            isDisabled={disabledInput}
            defaultOption={getDefaultVal(
              consumerFactors.incomeType,
              (val, language) => getIncomeTypeLabel(val, language)
            )}
            maxWidth="100%"
          />
          <ImpInput
            {...incomeInputProps}
            errorMessage={
              checkConsumerDobOver21(consumerInfo.consumerProfile.dob)
                ? language.invalidHouseholdIncome
                : language.invalidIncome
            }
            label={
              checkConsumerDobOver21(consumerInfo.consumerProfile.dob)
                ? language.annualHouseholdIncome
                : language.annualIncome
            }
            isDisabled={disabledInput}
            focusContent={<IncomeDisclosure />}
            type="tel"
            instrumentation={() => {
              ANALYTICS.logEvent(EVENT.INCOME_HOUSING_FORM_FILLED_OUT, {
                field: 'annual_income',
              });
            }}
            maxWidth="100%"
          />
          <ImpSelect
            label={language.housingType}
            id="housingType"
            options={language.housingTypeOption.map(
              (val: string, index: number) => {
                return { value: housingTypes[index], label: val };
              }
            )}
            onOptionChange={(val) => {
              ANALYTICS.logEvent(EVENT.INCOME_HOUSING_FORM_FILLED_OUT, {
                field: 'housing_type',
              });
              setHousingType(val);
            }}
            isDisabled={disabledInput}
            defaultOption={getDefaultVal(
              consumerFactors.housingType,
              (val, language) => getHousingTypeLabel(val, language)
            )}
            maxWidth="100%"
          />

          <ImpInput
            {...housingCostInputProps}
            label={language.monthlyHousingCost}
            isDisabled={disabledInput}
            instrumentation={() => {
              ANALYTICS.logEvent(EVENT.INCOME_HOUSING_FORM_FILLED_OUT, {
                field: 'housing_cost',
              });
            }}
            type="tel"
            focusContent={<HousingDisclosure />}
            maxWidth="100%"
          />
          {!isMultiCardApplicationFlow && ssnView()}
        </ImpFlex>

        <ImpSpacer height="xl" />

        <SSADisclosure
          checked={ssaChecked}
          handleChange={(e) => {
            setSsaChecked(e.target.checked);
            // set to sessionStorage so it can be retrieved from OtpVerify page
            sessionStorage.setItem(
              SESSION_STORAGE.SSA_DISCLOSURE_ACCEPTED,
              String(e.target.checked)
            );
          }}
        />

        <ImpSpacer height={'xl'} />
        <ImpSpacer height={'l'} />

        <ImpFlex direction="column" gap={spacing.l}>
          <ImpText typography="body3" color={colors.content.onSurfaceVariant}>
            {language.byClickingSubmit}
          </ImpText>

          <ImpButton
            size="fill"
            variant="primary"
            buttonStyle="branded"
            onClick={handleSubmit}
            onMouseDown={(e) => {
              // prevents focus capture, which causes layout shifting
              e.preventDefault();
            }}
            state={submitButtonState}
            type="submit"
          >
            {language.submitBtn}
          </ImpButton>
        </ImpFlex>
        <ImpSpacer height="xl" />
      </form>
    </ImpContainer>
  );
}

const SSADisclosure = ({
  checked,
  handleChange,
}: {
  checked: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => unknown;
}) => {
  const { language } = useImpState();

  return (
    <ImpContainer>
      <ImpText typography="body3" color={colors.content.onSurface}>
        {language.ssaDisclosure1}
      </ImpText>

      <ImpSpacer height="xs" />

      <ImpText typography="body3" color={colors.content.onSurfaceVariant}>
        {language.ssaDisclosure2}
      </ImpText>

      <ImpSpacer height="m" />

      <ImpFlex direction="row" align="center" gap={spacing.xs}>
        <input
          type="checkbox"
          id="ssa_consent_checkbox"
          checked={checked}
          onChange={handleChange}
          style={{
            accentColor: 'var(--merchant-color)',
            height: '24px',
            width: '24px',
          }}
        />
        <label htmlFor="ssa_consent_checkbox">
          <ImpText typography="body3">{language.ssaCheckbox}</ImpText>
        </label>
      </ImpFlex>
    </ImpContainer>
  );
};

// Custom Hook to save consumer factors on unmount
const useSaveOnUnmount = (consumerFactor: ConsumerFactors) => {
  // Step 1: Create a ref to store the latest save function
  const saveConsumerFactorRef = useRef(() => {});
  const { dispatch } = useImpState();

  // Step 2: Update the ref whenever the values change
  useEffect(() => {
    saveConsumerFactorRef.current = () => {
      dispatch(setConsumerFactor(consumerFactor));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerFactor]);

  // Step 3: Set up the cleanup effect to only run on unmount
  useEffect(() => () => saveConsumerFactorRef.current(), []);
};
